/**
 * Service pour gérer les traductions multilingues
 */

import { WidgetLanguage } from "../types/widgets.d";
import { LibelleMultilingue } from "../types/apidae.d";

export class TranslationService {
  /**
   * Extrait le libellé dans la langue demandée
   */
  static getLabel(
    libelle: LibelleMultilingue | undefined,
    language: WidgetLanguage,
  ): string {
    if (!libelle) return "";

    // Map des clés de libellé selon la langue
    const languageKeys: Record<WidgetLanguage, keyof LibelleMultilingue> = {
      [WidgetLanguage.FR]: "libelleFr",
      [WidgetLanguage.EN]: "libelleEn",
      [WidgetLanguage.ES]: "libelleEs",
      [WidgetLanguage.IT]: "libelleIt",
      [WidgetLanguage.DE]: "libelleDe",
      [WidgetLanguage.NL]: "libelleNl",
      [WidgetLanguage.RU]: "libelleRu",
      [WidgetLanguage.ZH]: "libelleZh",
      [WidgetLanguage.PT_BR]: "libellePtBR",
      [WidgetLanguage.JA]: "libelleJa",
      [WidgetLanguage.KO]: "libelleKo",
    };

    const key = languageKeys[language];
    const value = libelle[key];

    // Fallback sur le français si la traduction n'existe pas
    return value || libelle.libelleFr || "";
  }

  /**
   * Extrait le libellé avec fallback automatique
   */
  static getLabelWithFallback(
    libelle: LibelleMultilingue | undefined,
    language: WidgetLanguage,
    fallbackLanguages: WidgetLanguage[] = [
      WidgetLanguage.FR,
      WidgetLanguage.EN,
    ],
  ): string {
    if (!libelle) return "";

    // Essayer la langue demandée
    const primary = this.getLabel(libelle, language);
    if (primary) return primary;

    // Essayer les fallbacks
    for (const fallback of fallbackLanguages) {
      const value = this.getLabel(libelle, fallback);
      if (value) return value;
    }

    // Retourner la première valeur disponible
    return Object.values(libelle).find((v) => v) || "";
  }

  /**
   * Traduit les chaînes de l'interface
   */
  static translate(key: string, language: WidgetLanguage): string {
    const translations: Record<WidgetLanguage, Record<string, string>> = {
      [WidgetLanguage.FR]: {
        loading: "Chargement...",
        error: "Erreur",
        noResults: "Aucun résultat",
        sortBy: "Trier par",
        filters: "Filtres",
        filterBy: "Filtrer par :",
        reset: "Réinitialiser",
        apply: "Appliquer",
        name: "Nom",
        commune: "Commune",
        date: "Date",
        previous: "Précédent",
        next: "Suivant",
        showMore: "Voir plus",
        showLess: "Voir moins",
        loadMore: "Charger plus",
        website: "Site web",
        openingHours: "Horaires",
        contact: "Contact",
        description: "Description",
        informations: "Informations",
        toggleFilters: "Afficher/Masquer les filtres",
        clear: "Effacer",
        clearFilters: "Effacer les filtres",
        list: "Liste",
        map: "Carte",
        agenda: "Agenda",
        searchInThisArea: "Rechercher dans cette zone",
        results: "Résultats",
        result: "Résultat",
        duration: "Durée",
        free: "Gratuit",
        animals: "Animaux",
        classification: "Classement",
        activities: "Activités",
        type: "Type",
        audience: "Public",
        labels: "Labels",
        distance: "Distance",
        opening: "Ouverture",
        reservation: "Réservation",
        dateStartPlaceholder: "Date de début",
        dateEndPlaceholder: "Date de fin",
        communePlaceholder: "Nom de la commune",
        selected: "sélectionné",
        selectedPlural: "sélectionnés",
        dateFrom: "De",
        dateTo: "À",
        periodFrom: "Du",
        periodTo: "au",
        clearDateStart: "Effacer la date de début",
        clearDateEnd: "Effacer la date de fin",
        clearSearch: "Effacer la recherche",
        noResultsFound: "Aucun résultat trouvé.",
        adjustSearchWithFilters: "Ajustez votre recherche grâce aux filtres.",
        noEventsWithDate: "Aucun événement avec date disponible",
        viewDetail: "Voir le détail",
        widgetErrorTitle: "Erreur de récupération du widget Apidae.",
        widgetErrorDescription:
          "Ce widget n'est pas autorisé pour ce site internet. Veuillez modifier sa configuration ou modifier son intégration.",
      },
      [WidgetLanguage.EN]: {
        loading: "Loading...",
        error: "Error",
        noResults: "No results",
        sortBy: "Sort by",
        filters: "Filters",
        filterBy: "Filter by:",
        reset: "Reset",
        apply: "Apply",
        name: "Name",
        commune: "City",
        date: "Date",
        previous: "Previous",
        next: "Next",
        showMore: "Show more",
        showLess: "Show less",
        loadMore: "Load more",
        website: "Website",
        openingHours: "Opening hours",
        contact: "Contact",
        description: "Description",
        informations: "Information",
        toggleFilters: "Show/Hide filters",
        clear: "Clear",
        clearFilters: "Clear filters",
        list: "List",
        map: "Map",
        agenda: "Agenda",
        searchInThisArea: "Search in this area",
        results: "Results",
        result: "Result",
        duration: "Duration",
        free: "Free",
        animals: "Animals",
        classification: "Classification",
        activities: "Activities",
        type: "Type",
        audience: "Audience",
        labels: "Labels",
        distance: "Distance",
        opening: "Opening",
        reservation: "Reservation",
        dateStartPlaceholder: "Start date",
        dateEndPlaceholder: "End date",
        communePlaceholder: "City name",
        selected: "selected",
        selectedPlural: "selected",
        dateFrom: "From",
        dateTo: "To",
        periodFrom: "From",
        periodTo: "to",
        clearDateStart: "Clear start date",
        clearDateEnd: "Clear end date",
        clearSearch: "Clear search",
        noResultsFound: "No results found.",
        adjustSearchWithFilters: "Adjust your search using the filters.",
        noEventsWithDate: "No events with available date",
        viewDetail: "View details",
        widgetErrorTitle: "Error retrieving Apidae widget.",
        widgetErrorDescription:
          "This widget is not authorized for this website. Please modify its configuration or modify its integration.",
      },
      [WidgetLanguage.ES]: {
        loading: "Cargando...",
        error: "Error",
        noResults: "Sin resultados",
        sortBy: "Ordenar por",
        filters: "Filtros",
        filterBy: "Filtrar por:",
        reset: "Restablecer",
        apply: "Aplicar",
        name: "Nombre",
        commune: "Ciudad",
        date: "Fecha",
        previous: "Anterior",
        next: "Siguiente",
        showMore: "Ver más",
        showLess: "Ver menos",
        loadMore: "Cargar más",
        website: "Sitio web",
        openingHours: "Horario",
        contact: "Contacto",
        description: "Descripción",
        informations: "Información",
        toggleFilters: "Mostrar/Ocultar filtros",
        clear: "Limpiar",
        clearFilters: "Limpiar filtros",
        list: "Lista",
        map: "Mapa",
        agenda: "Agenda",
        searchInThisArea: "Buscar en esta zona",
        results: "Resultados",
        result: "Resultado",
        duration: "Duración",
        free: "Gratis",
        animals: "Animales",
        classification: "Clasificación",
        activities: "Actividades",
        type: "Tipo",
        audience: "Público",
        labels: "Etiquetas",
        distance: "Distancia",
        opening: "Apertura",
        reservation: "Reserva",
        dateStartPlaceholder: "Fecha de inicio",
        dateEndPlaceholder: "Fecha de fin",
        communePlaceholder: "Nombre de la ciudad",
        selected: "seleccionado",
        selectedPlural: "seleccionados",
        dateFrom: "De",
        dateTo: "A",
        periodFrom: "Del",
        periodTo: "al",
        clearDateStart: "Borrar fecha de inicio",
        clearDateEnd: "Borrar fecha de fin",
        clearSearch: "Borrar búsqueda",
        noResultsFound: "No se encontraron resultados.",
        adjustSearchWithFilters: "Ajuste su búsqueda usando los filtros.",
        noEventsWithDate: "No hay eventos con fecha disponible",
        viewDetail: "Ver detalles",
        widgetErrorTitle: "Error al recuperar el widget Apidae.",
        widgetErrorDescription:
          "Este widget no está autorizado para este sitio web. Por favor, modifique su configuración o modifique su integración.",
      },
      [WidgetLanguage.IT]: {
        loading: "Caricamento...",
        error: "Errore",
        noResults: "Nessun risultato",
        sortBy: "Ordina per",
        filters: "Filtri",
        filterBy: "Filtra per:",
        reset: "Ripristina",
        apply: "Applica",
        name: "Nome",
        commune: "Comune",
        date: "Data",
        previous: "Precedente",
        next: "Successivo",
        showMore: "Mostra di più",
        showLess: "Mostra di meno",
        loadMore: "Carica altro",
        website: "Sito web",
        openingHours: "Orari",
        contact: "Contatto",
        description: "Descrizione",
        informations: "Informazione",
        toggleFilters: "Mostra/Nascondi filtri",
        clear: "Cancella",
        clearFilters: "Cancella filtri",
        list: "Elenco",
        map: "Mappa",
        agenda: "Agenda",
        searchInThisArea: "Cerca in questa zona",
        results: "Risultati",
        result: "Risultato",
        duration: "Durata",
        free: "Gratuito",
        animals: "Animali",
        classification: "Classificazione",
        activities: "Attività",
        type: "Tipo",
        audience: "Pubblico",
        labels: "Etichette",
        distance: "Distanza",
        opening: "Apertura",
        reservation: "Prenotazione",
        dateStartPlaceholder: "Data di inizio",
        dateEndPlaceholder: "Data di fine",
        communePlaceholder: "Nome del comune",
        selected: "selezionato",
        selectedPlural: "selezionati",
        dateFrom: "Da",
        dateTo: "A",
        periodFrom: "Dal",
        periodTo: "al",
        clearDateStart: "Cancella data di inizio",
        clearDateEnd: "Cancella data di fine",
        clearSearch: "Cancella ricerca",
        noResultsFound: "Nessun risultato trovato.",
        adjustSearchWithFilters: "Regola la tua ricerca utilizzando i filtri.",
        noEventsWithDate: "Nessun evento con data disponibile",
        viewDetail: "Vedi dettagli",
        widgetErrorTitle: "Errore nel recupero del widget Apidae.",
        widgetErrorDescription:
          "Questo widget non è autorizzato per questo sito web. Si prega di modificare la sua configurazione o modificare la sua integrazione.",
      },
      [WidgetLanguage.DE]: {
        loading: "Laden...",
        error: "Fehler",
        noResults: "Keine Ergebnisse",
        sortBy: "Sortieren nach",
        filters: "Filter",
        filterBy: "Filtern nach:",
        reset: "Zurücksetzen",
        apply: "Anwenden",
        name: "Name",
        commune: "Gemeinde",
        date: "Datum",
        previous: "Zurück",
        next: "Weiter",
        showMore: "Mehr anzeigen",
        showLess: "Weniger anzeigen",
        loadMore: "Mehr laden",
        website: "Webseite",
        openingHours: "Öffnungszeiten",
        contact: "Kontakt",
        description: "Beschreibung",
        informations: "Information",
        toggleFilters: "Filter anzeigen/ausblenden",
        clear: "Löschen",
        clearFilters: "Filter löschen",
        list: "Liste",
        map: "Karte",
        agenda: "Agenda",
        searchInThisArea: "In diesem Bereich suchen",
        results: "Ergebnisse",
        result: "Ergebnis",
        duration: "Dauer",
        free: "Kostenlos",
        animals: "Tiere",
        classification: "Klassifizierung",
        activities: "Aktivitäten",
        type: "Typ",
        audience: "Publikum",
        labels: "Labels",
        distance: "Entfernung",
        opening: "Öffnung",
        reservation: "Reservierung",
        dateStartPlaceholder: "Startdatum",
        dateEndPlaceholder: "Enddatum",
        communePlaceholder: "Gemeindename",
        selected: "ausgewählt",
        selectedPlural: "ausgewählt",
        dateFrom: "Von",
        dateTo: "Bis",
        periodFrom: "Vom",
        periodTo: "bis",
        clearDateStart: "Startdatum löschen",
        clearDateEnd: "Enddatum löschen",
        clearSearch: "Suche löschen",
        noResultsFound: "Keine Ergebnisse gefunden.",
        adjustSearchWithFilters: "Passen Sie Ihre Suche mit den Filtern an.",
        noEventsWithDate: "Keine Veranstaltungen mit verfügbarem Datum",
        viewDetail: "Details anzeigen",
        widgetErrorTitle: "Fehler beim Abrufen des Apidae-Widgets.",
        widgetErrorDescription:
          "Dieses Widget ist für diese Website nicht autorisiert. Bitte ändern Sie die Konfiguration oder die Integration.",
      },
      // Autres langues avec fallback sur l'anglais
      [WidgetLanguage.NL]: {
        loading: "Laden...",
        error: "Fout",
        noResults: "Geen resultaten",
        sortBy: "Sorteren op",
        filters: "Filters",
        filterBy: "Filteren op:",
        reset: "Resetten",
        apply: "Toepassen",
        name: "Naam",
        commune: "Gemeente",
        date: "Datum",
        previous: "Vorige",
        next: "Volgende",
        showMore: "Toon meer",
        showLess: "Toon minder",
        loadMore: "Meer laden",
        website: "Website",
        openingHours: "Openingstijden",
        contact: "Contact",
        description: "Beschrijving",
        informations: "Informatie",
        toggleFilters: "Filters tonen/verbergen",
        clear: "Wissen",
        clearFilters: "Filters wissen",
        list: "Lijst",
        map: "Kaart",
        agenda: "Agenda",
        searchInThisArea: "Zoeken in dit gebied",
        results: "Resultaten",
        result: "Resultaat",
        duration: "Duur",
        free: "Gratis",
        animals: "Dieren",
        classification: "Classificatie",
        activities: "Activiteiten",
        type: "Type",
        audience: "Publiek",
        labels: "Labels",
        distance: "Afstand",
        opening: "Opening",
        reservation: "Reservering",
        dateStartPlaceholder: "Startdatum",
        dateEndPlaceholder: "Einddatum",
        communePlaceholder: "Gemeentenaam",
        selected: "geselecteerd",
        selectedPlural: "geselecteerd",
        dateFrom: "Van",
        dateTo: "Tot",
        periodFrom: "Van",
        periodTo: "tot",
        clearDateStart: "Startdatum wissen",
        clearDateEnd: "Einddatum wissen",
        clearSearch: "Zoekopdracht wissen",
        noResultsFound: "Geen resultaten gevonden.",
        adjustSearchWithFilters: "Pas uw zoekopdracht aan met de filters.",
        noEventsWithDate: "Geen evenementen met beschikbare datum",
        viewDetail: "Details bekijken",
        widgetErrorTitle: "Fout bij het ophalen van de Apidae-widget.",
        widgetErrorDescription:
          "Deze widget is niet geautoriseerd voor deze website. Wijzig de configuratie of de integratie.",
      },
      [WidgetLanguage.RU]: {
        loading: "Загрузка...",
        error: "Ошибка",
        noResults: "Нет результатов",
        sortBy: "Сортировать по",
        filters: "Фильтры",
        filterBy: "Фильтровать по:",
        reset: "Сбросить",
        apply: "Применить",
        name: "Название",
        commune: "Город",
        date: "Дата",
        previous: "Предыдущий",
        next: "Следующий",
        showMore: "Показать больше",
        showLess: "Показать меньше",
        loadMore: "Загрузить больше",
        website: "Веб-сайт",
        openingHours: "Часы работы",
        contact: "Контакты",
        description: "Описание",
        informations: "Информация",
        toggleFilters: "Показать/Скрыть фильтры",
        clear: "Очистить",
        clearFilters: "Очистить фильтры",
        list: "Список",
        map: "Карта",
        agenda: "Повестка дня",
        searchInThisArea: "Искать в этой области",
        results: "Результаты",
        result: "Результат",
        duration: "Продолжительность",
        free: "Бесплатно",
        animals: "Животные",
        classification: "Классификация",
        activities: "Мероприятия",
        type: "Тип",
        audience: "Аудитория",
        labels: "Метки",
        distance: "Расстояние",
        opening: "Открытие",
        reservation: "Бронирование",
        dateStartPlaceholder: "Дата начала",
        dateEndPlaceholder: "Дата окончания",
        communePlaceholder: "Название города",
        selected: "выбрано",
        selectedPlural: "выбрано",
        dateFrom: "От",
        dateTo: "До",
        periodFrom: "С",
        periodTo: "до",
        clearDateStart: "Очистить дату начала",
        clearDateEnd: "Очистить дату окончания",
        clearSearch: "Очистить поиск",
        noResultsFound: "Результаты не найдены.",
        adjustSearchWithFilters: "Настройте поиск с помощью фильтров.",
        noEventsWithDate: "Нет событий с доступной датой",
        viewDetail: "Просмотр деталей",
        widgetErrorTitle: "Ошибка получения виджета Apidae.",
        widgetErrorDescription:
          "Этот виджет не авторизован для этого веб-сайта. Пожалуйста, измените его конфигурацию или измените его интеграцию.",
      },
      [WidgetLanguage.ZH]: {
        loading: "加载中...",
        error: "错误",
        noResults: "无结果",
        sortBy: "排序方式",
        filters: "筛选",
        filterBy: "筛选条件：",
        reset: "重置",
        apply: "应用",
        name: "名称",
        commune: "城市",
        date: "日期",
        previous: "上一页",
        next: "下一页",
        showMore: "显示更多",
        showLess: "显示更少",
        loadMore: "加载更多",
        website: "网站",
        openingHours: "营业时间",
        contact: "联系方式",
        description: "描述",
        informations: "信息",
        toggleFilters: "显示/隐藏筛选",
        clear: "清除",
        clearFilters: "清除筛选",
        list: "列表",
        map: "地图",
        agenda: "日程",
        searchInThisArea: "在此区域搜索",
        results: "结果",
        result: "结果",
        duration: "时长",
        free: "免费",
        animals: "动物",
        classification: "分类",
        activities: "活动",
        type: "类型",
        audience: "受众",
        labels: "标签",
        distance: "距离",
        opening: "开放",
        reservation: "预订",
        dateStartPlaceholder: "开始日期",
        dateEndPlaceholder: "结束日期",
        communePlaceholder: "城市名称",
        selected: "已选择",
        selectedPlural: "已选择",
        dateFrom: "从",
        dateTo: "到",
        periodFrom: "从",
        periodTo: "到",
        clearDateStart: "清除开始日期",
        clearDateEnd: "清除结束日期",
        clearSearch: "清除搜索",
        noResultsFound: "未找到结果。",
        adjustSearchWithFilters: "使用筛选器调整您的搜索。",
        noEventsWithDate: "没有可用日期的事件",
        viewDetail: "查看详情",
        widgetErrorTitle: "检索 Apidae 小部件时出错。",
        widgetErrorDescription:
          "此小部件未授权用于此网站。请修改其配置或修改其集成。",
      },
      [WidgetLanguage.PT_BR]: {
        loading: "Carregando...",
        error: "Erro",
        noResults: "Nenhum resultado",
        sortBy: "Ordenar por",
        filters: "Filtros",
        filterBy: "Filtrar por:",
        reset: "Redefinir",
        apply: "Aplicar",
        name: "Nome",
        commune: "Cidade",
        date: "Data",
        previous: "Anterior",
        next: "Próximo",
        showMore: "Mostrar mais",
        showLess: "Mostrar menos",
        loadMore: "Carregar mais",
        website: "Site",
        openingHours: "Horário de funcionamento",
        contact: "Contato",
        description: "Descrição",
        informations: "Informação",
        toggleFilters: "Mostrar/Ocultar filtros",
        clear: "Limpar",
        clearFilters: "Limpar filtros",
        list: "Lista",
        map: "Mapa",
        agenda: "Agenda",
        searchInThisArea: "Buscar nesta área",
        results: "Resultados",
        result: "Resultado",
        duration: "Duração",
        free: "Gratuito",
        animals: "Animais",
        classification: "Classificação",
        activities: "Atividades",
        type: "Tipo",
        audience: "Público",
        labels: "Etiquetas",
        distance: "Distância",
        opening: "Abertura",
        reservation: "Reserva",
        dateStartPlaceholder: "Data de início",
        dateEndPlaceholder: "Data de término",
        communePlaceholder: "Nome da cidade",
        selected: "selecionado",
        selectedPlural: "selecionados",
        dateFrom: "De",
        dateTo: "Até",
        periodFrom: "Do",
        periodTo: "ao",
        clearDateStart: "Limpar data de início",
        clearDateEnd: "Limpar data de término",
        clearSearch: "Limpar pesquisa",
        noResultsFound: "Nenhum resultado encontrado.",
        adjustSearchWithFilters: "Ajuste sua pesquisa usando os filtros.",
        noEventsWithDate: "Nenhum evento com data disponível",
        viewDetail: "Ver detalhes",
        widgetErrorTitle: "Erro ao recuperar o widget Apidae.",
        widgetErrorDescription:
          "Este widget não está autorizado para este site. Por favor, modifique sua configuração ou modifique sua integração.",
      },
      [WidgetLanguage.JA]: {
        loading: "読み込み中...",
        error: "エラー",
        noResults: "結果なし",
        sortBy: "並び替え",
        filters: "フィルター",
        filterBy: "フィルター条件：",
        reset: "リセット",
        apply: "適用",
        name: "名前",
        commune: "都市",
        date: "日付",
        previous: "前へ",
        next: "次へ",
        showMore: "もっと見る",
        showLess: "折りたたむ",
        loadMore: "さらに読み込む",
        website: "ウェブサイト",
        openingHours: "営業時間",
        contact: "連絡先",
        description: "説明",
        informations: "情報",
        toggleFilters: "フィルター表示/非表示",
        clear: "クリア",
        clearFilters: "フィルターをクリア",
        list: "リスト",
        map: "地図",
        agenda: "予定",
        searchInThisArea: "このエリアで検索",
        results: "結果",
        result: "結果",
        duration: "期間",
        free: "無料",
        animals: "動物",
        classification: "分類",
        activities: "活動",
        type: "タイプ",
        audience: "対象",
        labels: "ラベル",
        distance: "距離",
        opening: "開館",
        reservation: "予約",
        dateStartPlaceholder: "開始日",
        dateEndPlaceholder: "終了日",
        communePlaceholder: "都市名",
        selected: "選択済み",
        selectedPlural: "選択済み",
        dateFrom: "から",
        dateTo: "まで",
        periodFrom: "から",
        periodTo: "まで",
        clearDateStart: "開始日をクリア",
        clearDateEnd: "終了日をクリア",
        clearSearch: "検索をクリア",
        noResultsFound: "結果が見つかりませんでした。",
        adjustSearchWithFilters: "フィルターを使用して検索を調整してください。",
        noEventsWithDate: "利用可能な日付のイベントがありません",
        viewDetail: "詳細を見る",
        widgetErrorTitle: "Apidaeウィジェットの取得エラー。",
        widgetErrorDescription:
          "このウィジェットはこのウェブサイトでは許可されていません。設定を変更するか、統合を変更してください。",
      },
      [WidgetLanguage.KO]: {
        loading: "로딩 중...",
        error: "오류",
        noResults: "결과 없음",
        sortBy: "정렬 기준",
        filters: "필터",
        filterBy: "필터 조건:",
        reset: "재설정",
        apply: "적용",
        name: "이름",
        commune: "도시",
        date: "날짜",
        previous: "이전",
        next: "다음",
        showMore: "더 보기",
        showLess: "접기",
        loadMore: "더 불러오기",
        website: "웹사이트",
        openingHours: "영업 시간",
        contact: "연락처",
        description: "설명",
        informations: "정보",
        toggleFilters: "필터 표시/숨기기",
        clear: "지우기",
        clearFilters: "필터 지우기",
        list: "목록",
        map: "지도",
        agenda: "일정",
        searchInThisArea: "이 영역에서 검색",
        results: "결과",
        result: "결과",
        duration: "기간",
        free: "무료",
        animals: "동물",
        classification: "분류",
        activities: "활동",
        type: "유형",
        audience: "대상",
        labels: "라벨",
        distance: "거리",
        opening: "개방",
        reservation: "예약",
        dateStartPlaceholder: "시작일",
        dateEndPlaceholder: "종료일",
        communePlaceholder: "도시 이름",
        selected: "선택됨",
        selectedPlural: "선택됨",
        dateFrom: "부터",
        dateTo: "까지",
        periodFrom: "부터",
        periodTo: "까지",
        clearDateStart: "시작일 지우기",
        clearDateEnd: "종료일 지우기",
        clearSearch: "검색 지우기",
        noResultsFound: "결과를 찾을 수 없습니다.",
        adjustSearchWithFilters: "필터를 사용하여 검색을 조정하세요.",
        noEventsWithDate: "사용 가능한 날짜가 있는 이벤트 없음",
        viewDetail: "자세히 보기",
        widgetErrorTitle: "Apidae 위젯을 가져오는 중 오류가 발생했습니다.",
        widgetErrorDescription:
          "이 위젯은 이 웹사이트에 대해 승인되지 않았습니다. 구성을 수정하거나 통합을 수정하세요.",
      },
    };

    return translations[language]?.[key] || key;
  }

  /**
   * Formate une date selon la locale
   */
  static formatDate(
    dateStr: string,
    language: WidgetLanguage,
    options?: Intl.DateTimeFormatOptions,
  ): string {
    const date = new Date(dateStr);
    const locale = this.getLocale(language);

    const defaultOptions: Intl.DateTimeFormatOptions = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };

    return date.toLocaleDateString(locale, options || defaultOptions);
  }

  /**
   * Retourne le code locale pour une langue donnée
   */
  static getLocale(language: WidgetLanguage): string {
    const locales: Record<WidgetLanguage, string> = {
      [WidgetLanguage.FR]: "fr-FR",
      [WidgetLanguage.EN]: "en-US",
      [WidgetLanguage.ES]: "es-ES",
      [WidgetLanguage.IT]: "it-IT",
      [WidgetLanguage.DE]: "de-DE",
      [WidgetLanguage.NL]: "nl-NL",
      [WidgetLanguage.RU]: "ru-RU",
      [WidgetLanguage.ZH]: "zh-CN",
      [WidgetLanguage.PT_BR]: "pt-BR",
      [WidgetLanguage.JA]: "ja-JP",
      [WidgetLanguage.KO]: "ko-KR",
    };

    return locales[language];
  }
}
